import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Package, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { productService } from '../../../services/productService'; // Use main productService
import { sellerService } from '../../../services/sellerService';
import { sellerProductService } from '../../../services/sellerProductService';
import { useAuth } from '../../../contexts/AuthContext'; // To get authenticated user ID
import SellerProductModal from './SellerProductModal'; // Re-use existing modal
import AddProductOfferingModal from './AddProductOfferingModal'; // NEW: Import new modal
import { formatPrice } from '../../../utils/formatters';
import Icon from '../../../components/AppIcon';
import Switch from '../../../components/ui/Switch';
import { imageUploadService } from '../../../utils/imageUploadService';
import { supabase } from '../../../lib/supabase';


export default function SellerProductManagement() {
  const { user } = useAuth(); // Get authenticated user
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false); // State for ProductModal (edit)
  const [showAddProductOfferingModal, setShowAddProductOfferingModal] = useState(false); // NEW: State for AddProductOfferingModal (create)
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [layoutMode, setLayoutMode] = useState(() => localStorage.getItem('seller_preferred_layout') || 'list'); // Symmetrical dynamic initial loading
  const [isCustomOrderingActive, setIsCustomOrderingActive] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault(); // Required to allow dropping
  };
  
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (sourceIndex === targetIndex) return;
    
    setProducts(prev => {
      const list = [...prev];
      const [movedItem] = list.splice(sourceIndex, 1);
      list.splice(targetIndex, 0, movedItem);
      return list;
    });
  };

  const handleToggleCustomOrdering = async (checked) => {
    setIsCustomOrderingActive(checked);
    if (checked) {
      // Symmetrical: Reset any active column sorting filters to prevent them from fighting with drag-and-drop locations!
      setSortConfig({ key: null, direction: 'asc' });
    } else {
      setIsSavingOrder(true);
      try {
        const promises = products.map((prod, idx) => 
          supabase
            .from('seller_products')
            .update({ display_order: idx })
            .eq('id', prod.id)
        );
        await Promise.all(promises);
        
        // Symmetrical: Refetch the list from the database to ensure the state sequence is perfectly synchronized and locked!
        if (user?.id) {
          await loadSellerAndProducts(user.id);
        }
      } catch (err) {
        console.error('Error saving products display sequence:', err);
      } finally {
        setIsSavingOrder(false);
      }
    }
  };
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const itemsPerPage = layoutMode === 'grid' ? 12 : 10; // Dynamic items count prevents trailing empty slots on cards grid row!

  useEffect(() => {
    setCurrentPage(1);
  }, [localSearchQuery, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    // Handle the special component sorting logic (4 states)
    if (key === 'components') {
       if (sortConfig.key === 'components' && sortConfig.direction === 'asc') direction = 'desc';
       else if (sortConfig.key === 'components' && sortConfig.direction === 'desc') direction = 'count-asc';
       else if (sortConfig.key === 'components' && sortConfig.direction === 'count-asc') direction = 'count-desc';
       else if (sortConfig.key === 'components' && sortConfig.direction === 'count-desc') direction = 'asc';
    }
    setSortConfig({ key, direction });
  };


  const handleToggleStatus = async (product) => {
    try {
      const newStatus = product.status === 'active' ? 'inactive' : 'active';
      // Optimistic update
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, status: newStatus } : p));
      
      const submitData = {
        status: newStatus
      };
      
      await sellerProductService.update(product.id, submitData);
    } catch (err) {
      console.error('Error toggling product status:', err);
      // Revert on error
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, status: product.status } : p));
      setError(err.message || 'Failed to update status');
    }
  };

  useEffect(() => {
    if (user?.id) { // Only load if user is authenticated
      loadSellerAndProducts(user.id);
    } else {
      setLoading(false);
      setError('You must be logged in to view your products.');
    }
  }, [user?.id]); // Re-run when user or search query changes

  const loadSellerAndProducts = async (userId) => {
    try {
      setLoading(true);
      setError('');
      
      const sellerAccount = await sellerService.getSellerAccountForUser(userId);

      if (!sellerAccount) {
        setError('Seller account not found for current user.');
        setProducts([]);
        setLoading(false);
        return;
      }

      const sellerProducts = await sellerProductService.getSellerProductsBySellerId(sellerAccount.id);
      
      setProducts(sellerProducts || []);
    } catch (err) {
      console.error('Load seller products error:', err);
      setError(err.message || 'Failed to load your products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = Array.isArray(products) ? products.filter(p => {
    if (!localSearchQuery) return true;
    const q = localSearchQuery.toLowerCase();
    return p.productName?.toLowerCase().includes(q) ||
           p.productSKU?.toLowerCase().includes(q) ||
           p.productUPC?.toLowerCase().includes(q) ||
           p.stockCode?.toLowerCase().includes(q);
  }) : [];


  let sortedProducts = [...filteredProducts];
  if (sortConfig.key) {
    sortedProducts.sort((a, b) => {
      let valA, valB;
      switch (sortConfig.key) {
        case 'product': valA = a.productName || ''; valB = b.productName || ''; break;
        case 'sku': valA = a.productSKU || ''; valB = b.productSKU || ''; break;
        case 'stockCode': valA = a.stockCode || ''; valB = b.stockCode || ''; break;
        case 'upc': valA = a.productUPC || ''; valB = b.productUPC || ''; break;
        case 'category': valA = a.categoryName || ''; valB = b.categoryName || ''; break;
        case 'price': valA = parseFloat(a.price || 0); valB = parseFloat(b.price || 0); break;
        case 'stock': valA = parseInt(a.stockAmount || 0); valB = parseInt(b.stockAmount || 0); break;
        case 'views': valA = parseInt(a.viewsCount || 0); valB = parseInt(b.viewsCount || 0); break;
        case 'status': valA = a.status || ''; valB = b.status || ''; break;
        case 'components':
          if (sortConfig.direction.startsWith('count')) {
            valA = parseInt(a.myCompCount || 0); valB = parseInt(b.myCompCount || 0);
          } else {
            valA = parseInt(a.productComponentCount || 0); valB = parseInt(b.productComponentCount || 0);
          }
          break;
        default: return 0;
      }
      
      if (valA < valB) return sortConfig.direction.endsWith('desc') ? 1 : -1;
      if (valA > valB) return sortConfig.direction.endsWith('desc') ? -1 : 1;
      return 0;
    });
  }

  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / itemsPerPage));
  const paginatedProducts = sortedProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const displayProductsList = isCustomOrderingActive ? sortedProducts : paginatedProducts;


  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product reference?')) return;
    
    try {
      await sellerProductService.delete(id);
      loadSellerAndProducts(user?.id);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleEdit = (sellerProduct) => {
    const productForModal = {
        // from seller_products
        id: sellerProduct.id,
        price: sellerProduct.price,
        stockQuantity: sellerProduct.stockAmount,
        sellProductOnly: sellerProduct.sellProductOnly, // NEW: Include listing-specific sellProductOnly toggle!
        stockCode: sellerProduct.stockCode, // NEW: Include stockCode
        status: sellerProduct.status,
        myCompCount: sellerProduct.myCompCount,
        description: sellerProduct.description, // Added description
        sellerImageUrls: sellerProduct.sellerImageUrls, // NEW: Include sellerImageUrls (array)
        isSelfManufacturing: sellerProduct.isSelfManufacturing,
        manufacturerId: sellerProduct.manufacturerId,
        keywords: sellerProduct.keywords,
        seasonalSetting: sellerProduct.seasonalSetting,
        holidaySetting: sellerProduct.holidaySetting,
        
        // from products
        productId: sellerProduct.productId,
        name: sellerProduct.productName,
        // The original product's description might be different, but we prioritize seller's override
        // description: sellerProduct.productDescription, // REMOVED
        sku: sellerProduct.productSKU,
        upc: sellerProduct.productUPC,
        categoryName: sellerProduct.categoryName,
        manufacturerName: sellerProduct.manufacturerName,
        thumbnailPath: sellerProduct.productThumbnailPath,
        gltfFilePath: sellerProduct.productGltfFilePath,
        isFeatured: sellerProduct.productIsFeatured,
        isCertified: sellerProduct.productIsCertified, // Inherited read-only
        globalStaffpick: sellerProduct.productStaffpick, // Inherited read-only
        staffpick: sellerProduct.sellerStaffpick, // Seller's own override
    };
    setSelectedProduct(productForModal);
    setShowProductModal(true);
  };

  const handleCreate = () => {
    setShowAddProductOfferingModal(true);
  };

  const handleProductModalClose = (shouldRefresh) => {
    setShowProductModal(false);
    setSelectedProduct(null);
    if (shouldRefresh && user?.id) {
      loadSellerAndProducts(user.id);
    }
  };

  const handleAddProductOfferingModalClose = (shouldRefresh) => {
    setShowAddProductOfferingModal(false);
    if (shouldRefresh && user?.id) {
      loadSellerAndProducts(user.id);
    }
  };

  // No view ratings in Seller Dashboard initially
  // const handleViewRatings = async (product) => { ... };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        {user?.id && (
          <button onClick={() => loadSellerAndProducts(user.id)} className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Your Products ({filteredProducts?.length})</h2>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search your products..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e?.target?.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Product Offering
          </button>
        </div>
      </div>

      {/* Symmetrical Drag and Drop Custom Sequencing Toggle Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-gray-50 border border-gray-200 p-4 rounded-xl justify-between text-left shadow-sm">
        <div className="flex items-center gap-2">
          <Switch 
            checked={isCustomOrderingActive}
            onCheckedChange={handleToggleCustomOrdering}
          />
          <div>
            <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wide">Configure Custom Display Order</h4>
            <p className="text-[10px] text-gray-500">Enable this toggle to drag & drop items to customize their display order. Turning it off instantly saves the order.</p>
          </div>
        </div>

        {/* Symmetrical Layout Mode Switcher Buttons */}
        <div className="flex items-center gap-1.5 self-end sm:self-auto bg-white p-1 rounded-lg border border-slate-200">
          <button 
            type="button" 
            onClick={() => setLayoutMode('grid')} 
            className={`px-2.5 py-1 rounded text-xs font-bold flex items-center gap-1 transition-all ${layoutMode === 'grid' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <Icon name="Grid" size={14} />
            Cards
          </button>
          <button 
            type="button" 
            onClick={() => setLayoutMode('list')} 
            className={`px-2.5 py-1 rounded text-xs font-bold flex items-center gap-1 transition-all ${layoutMode === 'list' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <Icon name="List" size={14} />
            List
          </button>
        </div>

        {isSavingOrder && (
          <span className="text-[10px] font-bold text-blue-600 animate-pulse">Saving display sequence...</span>
        )}
      </div>
      {/* Symmetrical Products Layout Rendering */}
      {layoutMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mt-4">
          {loading ? (
            <div className="col-span-full py-12 text-center text-gray-500">
              <Icon name="Package" className="w-12 h-12 mx-auto mb-3 text-gray-400 animate-pulse" />
              <p>Loading your products...</p>
            </div>
          ) : filteredProducts?.length === 0 ? (
            <div className="col-span-full py-12 text-center text-gray-500">
              <Icon name="Package" className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No products found for your account</p>
            </div>
          ) : (
            displayProductsList?.map((product, index) => {
              const imgUrl = product?.sellerImageUrls?.[0] 
                ? (product?.sellerImageUrls?.[0]?.startsWith('http') ? product?.sellerImageUrls?.[0] : imageUploadService.getPublicUrl(product?.sellerImageUrls?.[0], 'product-thumbnails'))
                : (product?.productThumbnailPath ? (product?.productThumbnailPath?.startsWith('http') ? product?.productThumbnailPath : imageUploadService.getPublicUrl(product?.productThumbnailPath, 'product-thumbnails')) : null);

              return (
                <div 
                  key={product?.id}
                  draggable={isCustomOrderingActive}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`flex flex-col relative bg-card overflow-hidden group hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border border-border rounded-lg text-left ${isCustomOrderingActive ? 'cursor-move ring-2 ring-blue-500/30 bg-blue-50/10' : ''}`}
                >
                  {/* Symmetrical Drag Handle or Index Badge */}
                  {isCustomOrderingActive ? (
                    <div className="absolute top-2 left-2 z-20 bg-blue-600 text-white rounded-lg p-1.5 flex items-center justify-center shadow-md cursor-move">
                      <Icon name="GripVertical" className="w-3.5 h-3.5" />
                    </div>
                  ) : (
                    <div className="absolute top-2 left-2 z-20 bg-black/60 backdrop-blur-sm text-white text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                      #{index + 1}
                    </div>
                  )}

                  {/* Thumbnail Image matching actual store catalog sizing and scaling */}
                  <div className="relative aspect-square overflow-hidden bg-muted flex items-center justify-center border-b border-border">
                    {imgUrl ? (
                      <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" src={imgUrl} alt={product?.productName} />
                    ) : (
                      <Icon name="Package" className="w-12 h-12 text-muted-foreground/30" />
                    )}
                  </div>

                  {/* Card Details */}
                  <div className="p-3 flex-1 flex flex-col justify-between space-y-2">
                    <div>
                      <h4 className="font-bold text-xs text-slate-800 line-clamp-1 mb-1" title={product?.productName}>{product?.productName}</h4>
                      <p className="text-[9px] text-slate-500 font-medium">SKU: <span className="font-semibold text-slate-700">{product?.productSKU || 'N/A'}</span></p>
                      <p className="text-[9px] text-slate-500 font-medium">Code: <span className="font-semibold text-slate-700 font-mono">{product?.stockCode || 'N/A'}</span></p>
                    </div>

                    <div className="flex justify-between items-center bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">
                      <div>
                        <p className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">Price</p>
                        <p className="font-black text-[11px] text-blue-600">{formatPrice(product?.price || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">Stock</p>
                        <p className={`font-black text-[11px] ${product?.stockAmount > 0 ? 'text-green-600' : 'text-red-500'}`}>{product?.status === 'active' ? (product?.stockAmount ?? '0') : 'NFS'}</p>
                      </div>
                    </div>

                    {/* Status Check Switch */}
                    <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                      <div className="flex items-center gap-1.5">
                        <Switch 
                          checked={product?.status === 'active'}
                          onCheckedChange={() => handleToggleStatus(product)}
                          disabled={isCustomOrderingActive}
                        />
                        <span className={`text-[9px] font-bold uppercase tracking-wider ${product?.status === 'active' ? 'text-green-600' : 'text-slate-400'}`}>
                          {product?.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <div className="flex items-center gap-1">
                        <button 
                          type="button"
                          onClick={() => handleEdit(product)}
                          disabled={isCustomOrderingActive}
                          className="p-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded border hover:text-blue-600 transition-colors"
                          title="Edit Product"
                        >
                          <Icon name="Edit" size={14} />
                        </button>
                        <button 
                          type="button"
                          onClick={() => handleDelete(product.id)}
                          disabled={isCustomOrderingActive}
                          className="p-1.5 bg-gray-50 hover:bg-red-50 text-red-500 rounded border border-red-100 hover:text-red-700 hover:border-red-200 transition-colors"
                          title="Delete Product"
                        >
                          <Icon name="Trash" size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {isCustomOrderingActive && <th className="w-10 px-4 py-3 bg-gray-100"></th>}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("product")}>Product {sortConfig.key === "product" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("sku")}>SKU {sortConfig.key === "sku" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("stockCode")}>Stock Code {sortConfig.key === "stockCode" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("upc")}>UPC {sortConfig.key === "upc" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("category")}>Category {sortConfig.key === "category" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("price")}>Price {sortConfig.key === "price" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("stock")}>QTY {sortConfig.key === "stock" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("components")} title="Click to sort by Total, then by Count">Components {sortConfig.key === "components" && (sortConfig.direction === "asc" ? "Total ↑" : sortConfig.direction === "desc" ? "Total ↓" : sortConfig.direction === "count-asc" ? "Count ↑" : "Count ↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("views")}>Views {sortConfig.key === "views" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => !isCustomOrderingActive && handleSort("status")}>Status {sortConfig.key === "status" && (sortConfig.direction === "asc" ? "↑" : "↓")}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center text-gray-500">
                      <Icon name="Package" className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>Loading your products...</p>
                    </td>
                  </tr>
                ) : filteredProducts?.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center text-gray-500">
                      <Icon name="Package" className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p>No products found for your account</p>
                    </td>
                  </tr>
                ) : (
                  displayProductsList?.map((product, index) => (
                    <tr 
                      key={product?.id} 
                      draggable={isCustomOrderingActive}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      className={`hover:bg-gray-50 transition-colors ${isCustomOrderingActive ? 'bg-blue-50/20' : ''}`}
                    >
                      {isCustomOrderingActive && (
                        <td className="px-4 py-4 text-center cursor-move text-gray-400 hover:text-gray-600 transition-colors">
                          <Icon name="GripVertical" size={16} />
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/3d-model-viewer?id=${product?.productId}`}
                          className="flex items-center group"
                          title="View in 3D Viewer"
                        >
                          <div className="h-10 w-10 flex-shrink-0 group-hover:scale-105 transition-transform duration-200">
                            {product?.sellerImageUrls?.[0] ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={product?.sellerImageUrls?.[0]?.startsWith('http') ? product?.sellerImageUrls?.[0] : imageUploadService.getPublicUrl(product?.sellerImageUrls?.[0], 'product-thumbnails')}
                                alt={product?.productName}
                              />
                            ) : product?.productThumbnailPath ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={product?.productThumbnailPath?.startsWith('http') ? product?.productThumbnailPath : imageUploadService.getPublicUrl(product?.productThumbnailPath, 'product-thumbnails')}
                                alt={product?.productName}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <Icon name="Package" className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">{product?.productName}</div>
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product?.productSKU}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                        {product?.stockCode || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product?.productUPC}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {product?.categoryName || 'Uncategorized'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatPrice(product?.price)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {product?.status === 'active' ? (product?.stockAmount ?? '0') : 'NFS'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {product?.myCompCount || 0} / {product?.productComponentCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold text-slate-700">
                        {product?.viewsCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-start gap-2">
                           <span className="text-xs text-gray-500 w-12 text-right">{product?.status === 'active' ? 'Active' : 'Inactive'}</span>
                           <Switch 
                              checked={product?.status === 'active'} 
                              onCheckedChange={() => handleToggleStatus(product)} 
                           />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          <Icon name="Edit2" className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product?.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Icon name="Trash2" className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Pagination Controls */}
      {filteredProducts.length > itemsPerPage && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-4 rounded-lg shadow">
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredProducts.length)}</span> of{' '}
                <span className="font-medium">{filteredProducts.length}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <span className="sr-only">Previous</span>
                  <Icon name="ChevronLeft" className="h-5 w-5" aria-hidden="true" />
                </button>
                <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                >
                  <span className="sr-only">Next</span>
                  <Icon name="ChevronRight" className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      {/* Modal for editing seller products */}
      {showProductModal && (
        <SellerProductModal
          product={selectedProduct}
          onClose={handleProductModalClose}
        />
      )}
      {/* NEW: AddProductOfferingModal */}
      {showAddProductOfferingModal && (
        <AddProductOfferingModal
          onClose={() => handleAddProductOfferingModalClose(false)} // Pass false as shouldRefresh by default if user just closes
          onProductAdded={() => handleAddProductOfferingModalClose(true)} // Refresh list on product added
        />
      )}
      {/* No Ratings Modal for Seller Dashboard initially */}
      {/* {showRatingsModal && ( ... )} */}
    </div>
  );
}